import { Container } from "@/components/ui/Container";
import { Eyebrow } from "@/components/ui/Eyebrow";

export type FaqItem = { q: string; a: string };

type FaqBlockProps = {
  id?: string;
  title: React.ReactNode;
  intro?: string;
  faqs: FaqItem[];
};

/** Builds a FAQPage JSON-LD node that matches the visible questions exactly. */
export function faqPageSchema(faqs: FaqItem[], id: string) {
  return {
    "@type": "FAQPage",
    "@id": id,
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/** FAQ list rendered as native details elements, so every answer is in the server HTML. */
export function FaqBlock({ id = "faq", title, intro, faqs }: FaqBlockProps) {
  return (
    <section id={id} className="border-b border-ink/[0.06] bg-surface-low/50 py-16 md:py-24">
      <Container>
        <div className="mx-auto max-w-3xl">
          <Eyebrow>FAQ</Eyebrow>
          <h2 className="mt-4 font-display text-display-lg font-medium text-ink text-balance">
            {title}
          </h2>
          {intro ? <p className="mt-5 text-lg text-ink-muted">{intro}</p> : null}

          <div className="mt-10 divide-y divide-ink/[0.08] rounded-2xl border border-ink/[0.08] bg-white">
            {faqs.map((f, i) => (
              <details key={f.q} className="group p-6 md:p-7" open={i === 0}>
                <summary className="flex cursor-pointer items-center justify-between gap-6 text-left">
                  <h3 className="font-display text-lg font-bold leading-snug tracking-tight text-ink">
                    {f.q}
                  </h3>
                  <span
                    aria-hidden="true"
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-ink/10 text-ink transition-all duration-300 group-open:rotate-45 group-open:bg-ink group-open:text-accent"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M7 1.5v11M1.5 7h11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </span>
                </summary>
                <p className="mt-4 text-ink-muted">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}
